import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {Button,Container,ModalComponent,SubTitle,Title,} from './styles'
import { useTheme } from 'styled-components'
import { api } from '../../../services/api'
import useToast from '../../../hooks/useToast'
import { useNavigate } from 'react-router-dom'

export interface ModalHandle {
  openModal: (fileUrl: string, documentInfo?: any) => void;
  closeModal: () => void;
}

const ModalViewer = forwardRef<ModalHandle>((_, ref) => {
  const [open, setOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [documentInfo, setDocumentInfo] = useState<any>(null);
  const [useDropboxViewer, setUseDropboxViewer] = useState(true); // Padrão: Dropbox Viewer
  const [showHistory, setShowHistory] = useState(false);
  const [showGovModal, setShowGovModal] = useState(false);
  const theme = useTheme();
  const toast = useToast();
  const navigate = useNavigate();

  const handleOcr = async () => {
    try {
      if (!documentInfo?._id) return
      toast.successToast('Processamento OCR iniciado! Você poderá buscar pelo conteúdo em breve.')
      await api.post(`/documents/${documentInfo._id}/ocr`)
    } catch (error: any) {
      toast.errorToast(error?.response?.data?.message || 'Erro ao iniciar OCR')
    }
  }

  const [isSigning, setIsSigning] = useState(false);

  const handleSignPdf = async () => {
    if (!documentInfo?._id || !fileUrl) return;
    try {
      setIsSigning(true);
      toast.successToast('Baixando PDF para assinatura...');
      
      const res = await api.get(`/documents/${documentInfo._id}/download`, {
        responseType: 'arraybuffer',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = async () => {
        try {
          const base64data = (reader.result as string).split(',')[1];
          toast.successToast('Aguardando Assinador SERPRO...');
          
          const { signPdfWithSerpro } = await import('../../../services/serpro.service');
          const signedBase64 = await signPdfWithSerpro(base64data);
          
          toast.successToast('Enviando documento assinado...');

          const byteCharacters = atob(signedBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const signedBlob = new Blob([byteArray], { type: 'application/pdf' });
          const signedFile = new File([signedBlob], documentInfo.name || 'documento_assinado.pdf', { type: 'application/pdf' });

          const formData = new FormData();
          formData.append('document', signedFile);
          formData.append('action', 'Assinatura Eletrônica (ICP-Brasil)');

          await api.post(`/documents/${documentInfo._id}/save-version`, formData);
          
          toast.successToast('Documento assinado com sucesso!');
          
          // Recarregar os dados do modal chamando as APIs necessárias ou fechando o modal
          setTimeout(() => {
            setOpen(false);
            window.location.reload();
          }, 1500);

        } catch (err: any) {
          toast.errorToast(err.message || 'Erro ao assinar com SERPRO.');
        } finally {
          setIsSigning(false);
        }
      };
    } catch (e) {
      console.error(e);
      toast.errorToast('Erro ao baixar documento para assinatura.');
      setIsSigning(false);
    }
  };

  useImperativeHandle(ref, () => ({
    openModal(url: string, info?: any) {
      setFileUrl(url);
      setDocumentInfo(info);
      setUseDropboxViewer(true); // Sempre usar Dropbox Viewer como padrão
      setOpen(true);
    },
    closeModal() {
      setOpen(false);
      setFileUrl(null);
      setDocumentInfo(null);
      setUseDropboxViewer(true); // Manter Dropbox Viewer como padrão
    },
  }));

  // Função para obter URL de download direto
  const getDownloadUrl = (url: string) => {
    // Se é URL do Nextcloud, converter para download direto
    if (isNextcloudUrl(url)) {
      // https://drive.nagelconsultoria.tec.br/index.php/s/TOKEN -> .../s/TOKEN/download
      if (url.includes('/s/') && !url.includes('/download')) {
        try {
          const urlObj = new URL(url);
          if (!urlObj.pathname.endsWith('/download')) {
            urlObj.pathname = urlObj.pathname.replace(/\/$/, '') + '/download';
          }
          return urlObj.toString();
        } catch (e) {
          return url.split('?')[0].replace(/\/$/, '') + '/download' + (url.includes('?') ? '?' + url.split('?')[1] : '');
        }
      }
      return url;
    }
    
    // Se já tem dl=1, retorna como está
    if (url.includes('dl=1')) return url;
    // Se tem dl=0, substitui por dl=1
    if (url.includes('dl=0')) return url.replace('dl=0', 'dl=1');
    // Se não tem parâmetro dl, adiciona
    return url + (url.includes('?') ? '&' : '?') + 'dl=1';
  };

  // Função para baixar arquivo com nome personalizado
  const downloadFileWithName = async (url: string, fileName: string) => {
    try {
      const downloadUrl = getDownloadUrl(url);
      
      // Método 1: Tentar download direto com fetch (recomendado)
      try {
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Accept': '*/*',
          },
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName; // Nome personalizado do banco
          link.style.display = 'none';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Limpar o blob URL após um delay
          setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
          }, 100);
          
          console.log(`Download iniciado: ${fileName}`);
          return;
        }
      } catch (fetchError) {
        console.log('Fetch falhou, tentando método alternativo:', fetchError);
      }
      
      // Método 2: Download direto com link (fallback)
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName; // Força o nome do arquivo
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Download iniciado (método 2): ${fileName}`);
      
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      // Fallback final: abrir em nova aba
      window.open(getDownloadUrl(url), '_blank', 'noopener,noreferrer');
    }
  };

  // Função para obter URL de visualização (para PDFs)
  const getPdfViewerUrl = (url: string) => {
    const downloadUrl = getDownloadUrl(url);
    return `https://docs.google.com/gview?url=${encodeURIComponent(downloadUrl)}&embedded=true`;
  };

  // Função para verificar se é URL do Nextcloud
  const isNextcloudUrl = (url: string) => {
    return url.includes('drive.nagelconsultoria.tec.br') || url.includes('nextcloud');
  };

  // Função para obter URL de visualização (para PDFs e Documentos)
  const getViewerUrl = (url: string, type: string) => {
    // Se é URL do Nextcloud
    if (isNextcloudUrl(url)) {
      // Documentos suportados pelo visualizador do Google
      if (['pdf', 'document', 'spreadsheet', 'presentation', 'text'].includes(type) || url.toLowerCase().includes('.pdf')) {
        return getPdfViewerUrl(url);
      }
      
      // Fallback para outros arquivos no Nextcloud (retorna apenas a URL, renderizando a UI do Nextcloud)
      return url;
    }
    
    // Logica original do Dropbox Viewer
    if (url.includes('dl=0')) {
      return url.replace('dl=0', 'raw=1');
    }
    if (url.includes('dl=1')) {
      return url.replace('dl=1', 'raw=1');
    }
    // Se não tem parâmetro dl, adiciona raw=1
    return url + (url.includes('?') ? '&' : '?') + 'raw=1';
  };

  // Função para detectar tipo de arquivo
  const getFileType = (url: string, mimetype?: string, fileName?: string) => {
    if (mimetype) {
      if (mimetype.includes('pdf')) return 'pdf';
      if (mimetype.includes('image')) return 'image';
      if (mimetype.includes('text')) return 'text';
      if (mimetype.includes('word') || mimetype.includes('document')) return 'document';
      if (mimetype.includes('excel') || mimetype.includes('spreadsheet')) return 'spreadsheet';
      if (mimetype.includes('powerpoint') || mimetype.includes('presentation')) return 'presentation';
    }
    
    const urlLower = url.toLowerCase();
    const nameLower = (fileName || '').toLowerCase();
    
    if (urlLower.includes('.pdf') || nameLower.includes('.pdf')) return 'pdf';
    if (urlLower.match(/\.(jpg|jpeg|png|gif)$/) || nameLower.match(/\.(jpg|jpeg|png|gif)$/)) return 'image';
    if (urlLower.match(/\.(txt|md)$/) || nameLower.match(/\.(txt|md)$/)) return 'text';
    if (urlLower.match(/\.(doc|docx)$/) || nameLower.match(/\.(doc|docx)$/)) return 'document';
    if (urlLower.match(/\.(xls|xlsx)$/) || nameLower.match(/\.(xls|xlsx)$/)) return 'spreadsheet';
    if (urlLower.match(/\.(ppt|pptx)$/) || nameLower.match(/\.(ppt|pptx)$/)) return 'presentation';
    
    return 'unknown';
  };

  // Função para obter tipo de documento amigável
  const getDocumentTypeLabel = (fileType: string, documentInfo?: any) => {
    // Priorizar o tipo de documento do modal de informações se disponível
    if (documentInfo?.documentTypeWithData?.documentType) {
      return documentInfo.documentTypeWithData.documentType;
    }
    
    // Fallback para detecção automática
    const typeLabels: { [key: string]: string } = {
      'pdf': 'PDF',
      'image': 'Imagem',
      'text': 'Texto',
      'document': 'Documento Word',
      'spreadsheet': 'Planilha Excel',
      'presentation': 'Apresentação PowerPoint',
      'unknown': 'Arquivo'
    };
    return typeLabels[fileType] || 'Arquivo';
  };

  // Função para verificar se arquivo é muito grande para visualização
  const isFileTooLarge = (fileType: string, size: number): boolean => {
    const MAX_PDF_SIZE = 25 * 1024 * 1024; // 25MB
    const MAX_IMAGE_SIZE = 50 * 1024 * 1024; // 50MB
    
    if (fileType === 'pdf' && size > MAX_PDF_SIZE) return true;
    if (fileType === 'image' && size > MAX_IMAGE_SIZE) return true;
    
    return false;
  };

  // Função para obter tamanho formatado de forma responsiva
  const getFormattedSize = (size: number): string => {
    if (size === 0) return '0 B';
    
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const k = 1024;
    const i = Math.floor(Math.log(size) / Math.log(k));
    
    // Limitar a 2 casas decimais e remover zeros desnecessários
    const value = (size / Math.pow(k, i)).toFixed(2);
    const cleanValue = parseFloat(value).toString();
    
    return `${cleanValue} ${units[i]}`;
  };

  // Função para obter limite recomendado
  const getRecommendedLimit = (fileType: string): string => {
    if (fileType === 'pdf') return '25 MB';
    if (fileType === 'image') return '50 MB';
    return 'N/A';
  };

  // Função para extrair extensão do arquivo da URL
  const getFileExtension = (url: string) => {
    try {
      // Tentar extrair extensão da URL
      const urlPath = new URL(url).pathname;
      const extension = urlPath.split('.').pop();
      
      if (extension && extension.length <= 5) {
        return `.${extension}`;
      }
      
      // Fallback: tentar extrair da URL diretamente
      const match = url.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
      if (match) {
        return `.${match[1]}`;
      }
      
      return ''; // Sem extensão encontrada
    } catch (error) {
      // Se não conseguir fazer parse da URL, tentar regex simples
      const match = url.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
      if (match) {
        return `.${match[1]}`;
      }
      return '';
    }
  };

  const fileType = getFileType(fileUrl || '', documentInfo?.mimetype, documentInfo?.name);

  return (
    <ModalComponent isOpen={open} onRequestClose={() => setOpen(false)} contentLabel="Visualizar Arquivo">
      <Container>
        {/* Header fixo */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '15px',
          borderBottom: '2px solid #f0f0f0'
        }}>
          <div>
            <Title>Visualização de Arquivo</Title>
            {fileUrl && <SubTitle>Arquivo do {isNextcloudUrl(fileUrl) ? 'Nextcloud' : 'Dropbox'}</SubTitle>}
          </div>
          <Button 
            onClick={() => setOpen(false)}
            style={{ 
              backgroundColor: theme.colors.primary, 
              color: 'white',
              padding: '10px 15px',
              fontSize: '14px',
              minWidth: 'auto'
            }}
          >
            ✕ Fechar
          </Button>
        </div>

        {/* Área de conteúdo flexível */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {fileUrl ? (
            <>
              {/* Botões de ação */}
              <div style={{ 
                marginBottom: '20px', 
                display: 'flex', 
                justifyContent: 'center',
                gap: '15px',
                flexWrap: 'wrap'
              }}>
                <Button 
                  onClick={() => {
                    // Usar o nome do banco de dados como prioridade
                    const fileName = documentInfo?.name || 'documento';
                    const fileExtension = getFileExtension(fileUrl || '');
                    
                    // Limpar o nome do arquivo (remover caracteres inválidos)
                    const cleanFileName = fileName.replace(/[<>:"/\\|?*]/g, '_');
                    const fullFileName = `${cleanFileName}${fileExtension}`;
                    
                    console.log('Download solicitado:', {
                      originalName: documentInfo?.name,
                      cleanFileName: cleanFileName,
                      extension: fileExtension,
                      fullFileName: fullFileName
                    });
                    
                    downloadFileWithName(fileUrl, fullFileName);
                  }}
                  style={{ 
                    backgroundColor: theme.colors.primary, 
                    color: 'white',
                    padding: '15px 30px',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  📥 Baixar Arquivo
                </Button>
                
                <Button 
                  onClick={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')}
                  style={{ 
                    backgroundColor: '#0066cc', 
                    color: 'white',
                    padding: '15px 30px',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  {fileUrl && isNextcloudUrl(fileUrl) ? '🔗 Abrir no Nextcloud' : '🔗 Abrir no Dropbox'}
                </Button>

                {documentInfo?._id && (
                  <Button 
                    onClick={handleOcr}
                    style={{ 
                      backgroundColor: '#28a745', 
                      color: 'white',
                      padding: '15px 30px',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    🔍 Aplicar OCR
                  </Button>
                )}

                {documentInfo?._id && (documentInfo?.name?.toLowerCase().endsWith('.pdf') || documentInfo?.mimetype === 'application/pdf') && (
                  <Button 
                    onClick={() => {
                      setOpen(false);
                      navigate(`/document/${documentInfo._id}`);
                    }}
                    style={{ 
                      backgroundColor: '#6c757d', 
                      color: 'white',
                      padding: '15px 30px',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    ✏️ Editar PDF (Páginas)
                  </Button>
                )}

                {documentInfo?._id && (documentInfo?.name?.toLowerCase().endsWith('.pdf') || documentInfo?.mimetype === 'application/pdf') && (
                  <Button 
                    onClick={() => { if (!isSigning) handleSignPdf() }}
                    style={{ 
                      backgroundColor: theme.colors.warning, 
                      color: 'white',
                      padding: '15px 30px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      opacity: isSigning ? 0.7 : 1,
                      pointerEvents: isSigning ? 'none' : 'auto'
                    }}
                  >
                    {isSigning ? '⏳ Assinando...' : '✍️ Assinar (SERPRO)'}
                  </Button>
                )}

                {documentInfo?._id && (documentInfo?.name?.toLowerCase().endsWith('.pdf') || documentInfo?.mimetype === 'application/pdf') && (
                  <Button 
                    onClick={() => setShowGovModal(true)}
                    style={{ 
                      backgroundColor: '#005b96', 
                      color: 'white',
                      padding: '15px 30px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                    }}
                  >
                    🇧🇷 Assinar (Gov.br)
                  </Button>
                )}
                
              </div>
            
            {/* Informações do documento */}
            {documentInfo && (
              <div style={{ 
                backgroundColor: '#ffffff', 
                padding: '15px', 
                borderRadius: '8px', 
                marginBottom: '15px',
                border: `1px solid ${theme.colors.primary}`,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: theme.colors.primary }}>
                  {documentInfo.name}
                </h4>
                <div style={{ display: 'flex', gap: '20px', fontSize: '14px', color: theme.colors.primary, flexWrap: 'wrap' }}>
                  {documentInfo.size && <span>📏 {getFormattedSize(documentInfo.size)}</span>}
                  <span>📁 {fileType.toUpperCase()}</span>
                  {/* Exibir número de páginas se disponível */}
                  {documentInfo.pages && documentInfo.pages > 0 && (
                    <span>📄 {documentInfo.pages} página{documentInfo.pages !== 1 ? 's' : ''}</span>
                  )}
                  {/* Exibir tipo de arquivo do banco de dados se disponível */}
                  {documentInfo.documentTypeWithData?.documentType?.name && (
                    <span>🏷️ <strong>{documentInfo.documentTypeWithData.documentType.name}</strong></span>
                  )}
                  
                  {/* Exibir campos de índices (metadata) se disponíveis */}
                  {documentInfo.documentTypeWithData?.metadata && documentInfo.documentTypeWithData.metadata.length > 0 && (
                    <>
                      {documentInfo.documentTypeWithData.metadata.map((meta: any, index: number) => (
                        <span key={index} style={{ 
                          fontSize: '12px',
                          backgroundColor: '#f8f9fa',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          border: `1px solid ${theme.colors.primary}20`
                        }}>
                          <strong style={{ color: theme.colors.primary }}>{meta.name}:</strong> {meta.value || 'Não informado'}
                        </span>
                      ))}
                    </>
                  )}
                </div>

                {/* Histórico de Versões */}
                {documentInfo.versions && documentInfo.versions.length > 0 && (
                  <div style={{ marginTop: '15px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                    <div 
                      onClick={() => setShowHistory(!showHistory)} 
                      style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <h5 style={{ margin: '0 0 0 0', color: '#666', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        🕒 Histórico de Versões
                        <span style={{ fontSize: '12px', transition: 'transform 0.2s', transform: showHistory ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                          ▶
                        </span>
                      </h5>
                    </div>
                    {showHistory && (
                      <div style={{ maxHeight: '150px', overflowY: 'auto', marginTop: '10px' }}>
                        {documentInfo.versions.map((version: any, index: number) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          padding: '8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '13px'
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <strong>{version.action || 'Modificação'}</strong>
                            <span style={{ color: '#888' }}>
                              {new Date(version.createdAt).toLocaleString()} • {getFormattedSize(version.size)} • {version.pages} pág.
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button 
                              onClick={() => downloadFileWithName(version.url, `v${index}_${documentInfo.name}`)}
                              style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#0066cc', color: 'white' }}
                            >
                              Baixar
                            </Button>
                            <Button 
                              onClick={async () => {
                                if (window.confirm('Tem certeza que deseja restaurar esta versão? O arquivo atual será substituído.')) {
                                  try {
                                    await api.post(`/documents/${documentInfo._id}/restore-version`, { versionIndex: index });
                                    toast.successToast('Versão restaurada com sucesso! Atualize a página.');
                                    setOpen(false);
                                  } catch (error: any) {
                                    toast.errorToast(error?.response?.data?.message || 'Erro ao restaurar versão');
                                  }
                                }
                              }}
                              style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#ff9800', color: 'white' }}
                            >
                              Restaurar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Área de visualização */}
            <div style={{ 
              border: `2px solid ${theme.colors.primary}`, 
              borderRadius: '8px', 
              padding: '20px', 
              textAlign: 'center',
              backgroundColor: '#ffffff',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
            }}>
              {/* Visualização universal com Dropbox Viewer */}
              {fileType === 'image' ? (
                <>
                  <div style={{ flex: 1, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <img 
                      src={getDownloadUrl(fileUrl)} 
                      alt="Visualização da imagem"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px',
                        objectFit: 'contain'
                      }}
                      onError={() => {
                        console.log('Erro ao carregar imagem');
                      }}
                    />
                  </div>
                  <p style={{ color: theme.colors.primary, fontSize: '14px', marginTop: '10px' }}>
                    Visualização da imagem. Use o botão acima para baixar o arquivo.
                  </p>
                </>
              ) : (
                <>

                  <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <iframe 
                      src={getViewerUrl(fileUrl || '', fileType)} 
                      width="100%" 
                      height="100%" 
                      title="Visualização do documento"
                      style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '4px',
                        flex: 1,
                        minHeight: '400px'
                      }}
                      onError={() => {
                        console.log('Erro ao carregar arquivo via Dropbox Viewer');
                      }}
                    />
                  </div>
                  <p style={{ color: theme.colors.primary, fontSize: '14px', marginTop: '10px' }}>
                    Se não carregar, use os botões acima.
                  </p>
                </>
              )}
            </div>
            </>
          ) : (
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <p style={{ color: '#666', fontSize: '16px' }}>Nenhum documento selecionado ou erro ao carregar.</p>
          </div>
          )}
        </div>
      </Container>
      
      <ModalComponent isOpen={showGovModal} onRequestClose={() => setShowGovModal(false)} contentLabel="Autorização MGI">
        <Container style={{ maxWidth: '400px', margin: 'auto', textAlign: 'center', padding: '30px' }}>
          <h2 style={{ color: theme.colors.primary, marginBottom: '20px' }}>Integração Gov.br</h2>
          <div style={{ padding: '20px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '8px', marginBottom: '20px' }}>
            <strong>Aguardando autorização do MGI</strong>
            <p style={{ marginTop: '10px' }}>O fluxo de integração será liberado assim que o Ministério da Gestão e da Inovação fornecer o Client ID e Client Secret para este CNPJ.</p>
          </div>
          <Button 
            onClick={() => setShowGovModal(false)}
            style={{ backgroundColor: theme.colors.primary, color: 'white', padding: '10px 20px' }}
          >
            Fechar
          </Button>
        </Container>
      </ModalComponent>
    </ModalComponent>
  );
});

export default ModalViewer;
